import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Training } from '@precheckin/models/training/training.model';
import { TrainingCenter } from '@precheckin/models/training-center/training-center.model';
import { TrainingCenterConfiguration } from '@precheckin/models/training-center-configuration/training-center-configuration.model';
import { TrainingCenterService } from '@precheckin/services/training-center.service';
import { TrainingCentersConfigurationsService } from '@precheckin/services/training-centers-configurations.service';
import { TrainingsService } from '@precheckin/services/trainings.service';
import { take } from 'rxjs';
import { AppStoreKeys } from 'src/app/store/store-keys.module';

import {
  GetCurrentTraining,
  GetTraining,
  GetTrainingCenter,
  GetTrainingCenterConfiguration,
  IsTrainingReady
} from './precheckin-training.actions';
import {
  defaultTrainingState,
  IPreCheckinTrainingStateModel
} from './precheckin-training.model';
import { GetCheckinFormFields } from '../precheckin-form-customization/precheckin-form-customization.actions';
import { GetVisit } from '../visit/precheckin-visit.actions';

@State<IPreCheckinTrainingStateModel>({
  name: AppStoreKeys.PrecheckinTraining,
  defaults: defaultTrainingState
})
@Injectable()
export class PreCheckinTrainingState {
  constructor(
    private _trainingCentersConfigurationsService: TrainingCentersConfigurationsService,
    private _trainingService: TrainingsService,
    private _store: Store,
    private _trainingCenterService: TrainingCenterService
  ) {}

  @Selector()
  public static isTrainingReady(state: IPreCheckinTrainingStateModel): boolean {
    return state.isTrainingReady;
  }

  @Selector()
  static getTrainingCenterConfiguration(
    state: IPreCheckinTrainingStateModel
  ): TrainingCenterConfiguration {
    return state.trainingCenterConfiguration;
  }

  @Selector()
  static getTraining(state: IPreCheckinTrainingStateModel): Training {
    return state.training;
  }

  @Selector()
  static getCurrentTraining(state: IPreCheckinTrainingStateModel): Training {
    return state.currentTraining;
  }

  @Selector()
  static getTrainingCenter(
    state: IPreCheckinTrainingStateModel
  ): TrainingCenter {
    return state.trainingCenter;
  }

  @Action(GetCurrentTraining)
  getCurrentTraining(ctx: StateContext<IPreCheckinTrainingStateModel>): void {
    this._trainingService.getPrecheckinCurrentTraining().subscribe({
      next: (response) => {
        ctx.patchState({
          currentTraining: new Training(response),
          isTrainingReady: true
        });
      },
      error: () => {
        ctx.patchState({
          currentTraining: new Training(),
          isTrainingReady: true
        });
      }
    });
  }

  @Action(GetTraining)
  getTraining(
    ctx: StateContext<IPreCheckinTrainingStateModel>,
    { id }: GetTraining
  ): void {
    ctx.patchState({
      isTrainingReady: false
    });

    this._trainingService
      .getById(id)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this._store.dispatch(
            new GetTrainingCenterConfiguration(response.trainingCenterId)
          );

          this._store.dispatch(
            new GetTrainingCenter(response.trainingCenterId)
          );

          this._store.dispatch(
            new GetCheckinFormFields(response.trainingCenterId)
          );

          ctx.patchState({
            training: new Training(response),
            isTrainingReady: true
          });
        },
        error: () => {
          ctx.patchState({
            training: new Training(),
            isTrainingReady: true
          });
        }
      });
  }

  @Action(GetTrainingCenterConfiguration)
  getByTrainingCenterId(
    ctx: StateContext<IPreCheckinTrainingStateModel>,
    { geminiSiteId }: GetTrainingCenterConfiguration
  ): void {
    ctx.patchState({
      isTrainingReady: false
    });

    this._trainingCentersConfigurationsService
      .getByTrainingCenterId(geminiSiteId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          ctx.patchState({
            isTrainingReady: true,
            trainingCenterConfiguration: new TrainingCenterConfiguration(
              response
            )
          });

          const state = ctx.getState();
          if (state.training.visitId) {
            this._store.dispatch(new GetVisit(state.training.visitId));
          }
        },
        error: () => {
          ctx.patchState({
            trainingCenterConfiguration: new TrainingCenterConfiguration(),
            isTrainingReady: true
          });
        }
      });
  }

  @Action(IsTrainingReady)
  setIsReady(
    ctx: StateContext<IPreCheckinTrainingStateModel>,
    action: IsTrainingReady
  ): void {
    ctx.patchState({
      isTrainingReady: action.payload
    });
  }

  @Action(GetTrainingCenter)
  getTrainingCenterByTrainingCenterId(
    ctx: StateContext<IPreCheckinTrainingStateModel>,
    { geminiSiteId }: GetTrainingCenter
  ): void {
    ctx.patchState({
      isTrainingReady: false
    });

    this._trainingCenterService
      .getByTrainingCenterId(geminiSiteId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          ctx.patchState({
            isTrainingReady: true,
            trainingCenter: new TrainingCenter(response)
          });
        },
        error: () => {
          ctx.patchState({
            trainingCenter: new TrainingCenter(),
            isTrainingReady: true
          });
        }
      });
  }
}
